/**
 * Service for managing feature flags
 * 
 * Feature flags are loaded from the API at startup and cached in global state.
 * This allows enabling/disabling features without rebuilding the frontend.
 * 
 * Flags are fetched once when the user authenticates and stored in FeatureFlagContext.
 */
class FeatureFlagService {
    private flags: Record<string, boolean> = {};
    private flagsGetter: (() => Record<string, boolean>) | null = null;

    /**
     * Set the function to get flags from context
     * This is called by the FeatureFlagProvider to inject the context values
     */
    setFlagsGetter(getter: () => Record<string, boolean>): void {
        this.flagsGetter = getter;
    }

    /**
     * Get all feature flags from cached context values
     */
    private getFlags(): Record<string, boolean> {
        if (this.flagsGetter) {
            return this.flagsGetter();
        }
        // Fallback to empty object if context not available yet
        return {};
    }

    /**
     * Check if a feature flag is enabled
     * @param flagName - Name of the feature flag
     * @returns True if enabled, false otherwise
     */
    isEnabled(flagName: string): boolean {
        const flags = this.getFlags();
        return flags[flagName] ?? false;
    }

    /**
     * Get all feature flags
     * @returns Dictionary of flag names to enabled status
     */
    getAllFlags(): Record<string, boolean> {
        return this.getFlags();
    }
}

export const featureFlagService = new FeatureFlagService();
